import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import type { TriggerProps } from '@atlaskit/popup/types';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { usePublish } from '@atlaskit/rovo-triggers/main';

import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { ConfluenceAgentMenu } from './confluence-agent-menu/ConfluenceAgentMenu.tsx';

const messages = defineMessages({
	agentsPanelLabel: {
		id: 'object-sidebar-components.agents-panel.label',
		defaultMessage: 'Open Agents',
		description: 'Label for button to open the Rovo agents panel',
	},
	agentsPanelTooltip: {
		id: 'object-sidebar-components.agents-panel.tooltip',
		defaultMessage: 'Agents',
		description: "Tooltip shown when hovering the Object Sidebar's Agents button",
	},
});

export type AgentsPanelButtonProps = {
	contentId: string;
	isDisabled?: boolean;
};

export const AgentsPanelButton = ({
	contentId,
	isDisabled = false,
}: AgentsPanelButtonProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const publishRovo = usePublish('ai-mate');
	const [isOpen, setIsOpen] = useState(false);
	const [selectedAgentId, setSelectedAgentId] = useState<string | undefined>(undefined);
	const { Popup } = useChoreographerAssets();

	const handleOpen = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'agentsPanel',
				source: 'contentWrapper',
				attributes: {
					contentId,
				},
			},
		}).fire();

		setIsOpen((prev) => !prev);
	}, [contentId, createAnalyticsEvent]);

	const handleClose = useCallback(() => {
		setIsOpen(false);
	}, []);

	const handleAgentSelect = useCallback(
		(agent: Agent | undefined) => {
			if (!agent) return;
			setIsOpen(false);
			setSelectedAgentId(agent.id);
			try {
				publishRovo({
					type: 'chat-new',
					source: 'confluence-object-sidebar-agents-button',
					openChat: true,
					openChatMode: 'mini-modal',
					data: {
						dialogues: [],
						agentId: agent.id,
						sourceId: `confluence-object-sidebar:agents-button:${agent.id}`,
						mode: {
							useCurrentPageContext: true,
						},
					},
				});
			} catch (e) {
				// Fail silently - popup is already closed
			}
		},
		[publishRovo],
	);

	const renderContent = useCallback(
		() => (
			<ConfluenceAgentMenu
				cloudId={cloudId}
				selectedAgentId={selectedAgentId}
				onSelectAgent={handleAgentSelect}
				onBrowseAgents={handleClose}
			/>
		),
		[cloudId, selectedAgentId, handleAgentSelect, handleClose],
	);

	const renderTrigger = useCallback(
		(triggerProps?: TriggerProps) => (
			<Tooltip
				position="left"
				content={formatMessage(messages.agentsPanelTooltip)}
				delay={0}
				hideTooltipOnClick
			>
				<IconButton
					{...triggerProps}
					onClick={handleOpen}
					appearance="subtle"
					shape="default"
					icon={AiAgentIcon}
					label={formatMessage(messages.agentsPanelLabel)}
					testId="sidebar-agents-button"
					isDisabled={isDisabled}
					isSelected={isOpen}
					interactionName="open-agents-panel"
				/>
			</Tooltip>
		),
		[formatMessage, handleOpen, isDisabled, isOpen],
	);

	// cloudId is required for the agents API — render nothing if unavailable.
	// This check must come after all hooks to comply with React Rules of Hooks.
	if (!cloudId) {
		return null;
	}

	return (
		<ConditionalRelayEnvironmentProvider>
			<Popup
				isOpen={isOpen}
				onClose={handleClose}
				placement="left-start"
				content={renderContent}
				trigger={renderTrigger}
			/>
		</ConditionalRelayEnvironmentProvider>
	);
};
